import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { createOpenAIModalCommand } from '../decoration/actions';
import { aiEventHubPluginKey } from './ai-event-hub-plugin-key';
import { eventHub } from './event-hub';
import { EventHubPrompt } from './types';
import { firePromptConfigNotFoundError } from './utils/analytic-utils';
import { getConfigItem } from './utils/config-utils';
import { findEditorIdFromView } from './utils/isolated-utils';
import { handleStreamingService } from './utils/streaming-utils';

export const createAIEventHubPlugin = (options: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
}): SafePlugin => {
	const { aiGlobalOptIn } = options;

	return new SafePlugin({
		key: aiEventHubPluginKey,

		view: (editorView) => {
			let currentEditorView = editorView;

			const { unsubscribe } = eventHub.subscribe({
				event: 'start prompt',
				callback: (data) => {
					const editorId = findEditorIdFromView(currentEditorView);
					if (editorId === data.targetEditorId) {
						const { state, dispatch } = currentEditorView;

						let payloadData;
						if (data.prompt === EventHubPrompt.IssueReformatter) {
							payloadData = data.additionalPayloadDataIssueReformatter;
						} else if (data.prompt === EventHubPrompt.DraftReplyPill) {
							payloadData = data.additionalPayloadData;
						} else if (data.prompt === EventHubPrompt.DescriptionCompleter) {
							payloadData = data.additionalPayloadData;
						} else if (data.prompt === EventHubPrompt.IncidentSmartComments) {
							payloadData = data.additionalPayloadData;
						} else if (data.prompt === EventHubPrompt.SuggestComment) {
							payloadData = data.additionalPayloadData;
						}

						const configItem = getConfigItem(
							data.prompt,
							options.editorPluginAIProvider,
							data.adjustSelection,
							payloadData,
						);

						if (!configItem) {
							firePromptConfigNotFoundError({
								dispatch,
								editorView: currentEditorView,
								prompt: data.prompt,
								promptText:
									data.prompt === EventHubPrompt.CustomPrompt ? data.promptText : undefined,
								adjustSelection: data.adjustSelection,
							});
							return;
						}

						const params = {
							state,
							configItem: configItem,
							lastTriggeredFrom: data.analyticSourceId,
							willAISuggestEmoji: data.willAISuggestEmoji,
							aiGlobalOptIn,
							adjustSelection: data.adjustSelection,
							initialPrompt:
								data.prompt === EventHubPrompt.CustomPrompt ? data.promptText : undefined,
							locale: options.getIntl().locale,
						};

						if (data.hideModal) {
							handleStreamingService({
								getIntl: options.getIntl,
								currentEditorView,
								editorPluginAIProvider: options.editorPluginAIProvider,
								configItem,
								streamingCallback: data.streamingCallback,
								api: options.api,
								analyticSourceId: data.analyticSourceId,
								initialPrompt:
									data.prompt === EventHubPrompt.CustomPrompt ? data.promptText : undefined,
							});
						} else {
							const openAIModalCommand = createOpenAIModalCommand(params);
							openAIModalCommand(state, dispatch);
						}
					}
				},
			});

			return {
				update: (newEditorView) => {
					currentEditorView = newEditorView;
				},
				destroy: () => {
					unsubscribe();
				},
			};
		},
	});
};
